import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import SignatureDialog from '@/components/SignatureDialog';
import { CharteTexte, useCharteInfo } from '@/pages/Charte';
import { ShieldCheck, LogOut } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Écran obligatoire, à la première connexion et à chaque nouvelle version de
// la charte : lecture du texte, signature manuscrite (une simple case à
// cocher ne suffit pas) et enregistrement de la preuve côté serveur (texte
// exact signé + empreinte, signature, date, IP, navigateur, sceau). Tant que
// ce n'est pas fait, le serveur refuse aussi toutes les autres requêtes.
const CharteAcceptation = ({ user, onAccepted, onLogout }) => {
  const { info, error } = useCharteInfo();
  const texteRef = useRef(null);
  const [checked, setChecked] = useState(false);
  const [signature, setSignature] = useState(null);
  const [signing, setSigning] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const deja = user?.charte?.date;
  const dejaTxt = deja ? new Date(deja).toLocaleDateString('fr-FR') : null;

  const handleValidate = async () => {
    if (!checked) {
      toast.error('Merci de cocher la case pour confirmer votre lecture');
      return;
    }
    if (!signature) {
      toast.error('La signature est obligatoire pour valider la charte');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/charte`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature, texte: texteRef.current?.innerText || '' })
      });
      if (res.ok) {
        const updatedUser = await res.json();
        toast.success('Charte signée, merci !');
        onAccepted(updatedUser);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur lors de la validation');
      }
    } catch {
      toast.error('Erreur de connexion au serveur');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-4 px-3 sm:py-8">
      <div className="max-w-3xl mx-auto bg-white dark:bg-slate-900 rounded-2xl shadow-lg p-4 sm:p-8" data-testid="charte-acceptation">
        <div className="flex items-start justify-between gap-3">
          <h1 className="flex items-center gap-2 text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50">
            <ShieldCheck className="h-6 w-6 text-indigo-600 shrink-0" />
            Charte d'utilisation et protection des données
          </h1>
          <Button variant="outline" size="sm" onClick={onLogout} data-testid="charte-logout-btn" className="shrink-0">
            <LogOut className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Déconnexion</span>
          </Button>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-300 mt-2">
          Bienvenue {user?.name}.{' '}
          {dejaTxt
            ? `La charte a évolué depuis votre dernière signature (${dejaTxt}). `
            : 'Avant votre première utilisation, '}
          merci de lire la charte ci-dessous puis de la signer. L'application reste inaccessible tant qu'elle n'est pas signée.
        </p>

        <div
          ref={texteRef}
          className="mt-4 border rounded-lg p-4 bg-slate-50 dark:bg-slate-950 max-h-[55vh] overflow-y-auto"
          data-testid="charte-text"
        >
          {error && <p className="text-red-600">Impossible de charger la charte, rechargez la page.</p>}
          <CharteTexte info={info} />
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Cette charte reste consultable à tout moment : lien « Charte et données personnelles » en bas de chaque page.
        </p>

        <div className="flex items-start gap-2 pt-4">
          <Checkbox id="charte-checkbox" checked={checked} onCheckedChange={(v) => setChecked(!!v)} data-testid="charte-checkbox" />
          <label htmlFor="charte-checkbox" className="text-sm text-slate-700 dark:text-slate-200 cursor-pointer">
            J'ai lu et compris la charte d'utilisation et l'information sur mes données personnelles, et j'en accepte les règles.
          </label>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center pt-4">
          {signature ? (
            <div className="flex items-center gap-3">
              <img src={signature} alt="signature" className="h-14 border rounded bg-white" />
              <Button variant="outline" size="sm" onClick={() => setSigning(true)}>Refaire ma signature</Button>
            </div>
          ) : (
            <Button variant="outline" onClick={() => setSigning(true)} data-testid="charte-sign-btn">
              Signer à main levée
            </Button>
          )}
          <Button
            className="btn-primary flex-1"
            onClick={handleValidate}
            disabled={!checked || !signature || submitting || !info}
            data-testid="charte-validate-btn"
          >
            {submitting ? 'Enregistrement…' : 'Valider et signer la charte'}
          </Button>
        </div>
      </div>

      <SignatureDialog
        open={signing}
        onClose={() => setSigning(false)}
        onSign={(sig) => { setSignature(sig); setSigning(false); }}
        title="Signature de la charte d'utilisation"
      />
    </div>
  );
};

export default CharteAcceptation;
